import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, MapPin, ChevronRight } from "lucide-react";
import { MonthCalendar } from "@/components/MonthCalendar";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, fmtLongDate, todayISO, KIND } from "@/lib/format";
import { StatusBadge } from "@/components/Brand";

function JobCard({ job, i }) {
  return (
    <Link to={`/tecnico/intervento/${job.id}`} data-testid={`tech-job-card-${i}`}
      className="choice flex items-center gap-4 rounded-2xl border bg-white p-4">
      <div className="w-14 shrink-0 text-center">
        <p className="font-display text-xl font-bold text-slate-900">{job.start}</p>
        <p className="text-[10px] uppercase tracking-wider text-slate-400">{fmtDate(job.date, { day: "2-digit", month: "short" })}</p>
      </div>
      <div className="min-w-0 flex-1 border-l pl-4">
        <p className="truncate font-semibold">{job.client.first_name} {job.client.last_name}</p>
        <p className="truncate text-xs text-slate-500">{KIND[job.kind]}{job.equipment_label ? ` · ${job.equipment_label}` : ""}{job.end_date ? ` · fino al ${fmtDate(job.end_date, { day: "2-digit", month: "short" })}` : ""}</p>
        <p className="mt-1 flex items-center gap-1 truncate text-xs text-slate-500"><MapPin className="h-3 w-3" />{job.client.city}</p>
        <div className="mt-2 flex gap-1.5"><StatusBadge status={job.status} />{job.tech_ack === "in_attesa" && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800" data-testid={`tech-job-ack-badge-${i}`}>Da confermare</span>}</div>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-300" />
    </Link>
  );
}

function Section({ title, jobs, id, empty, highlight }) {
  return (
    <section className={`space-y-3 ${highlight ? "rounded-2xl border-2 border-[#61a229] bg-[#a2ca6b]/10 p-3" : ""}`} data-testid={id}>
      <p className={`font-display font-semibold ${highlight ? "text-[#3d6b16]" : ""}`}>{title} <span className="text-sm font-normal text-slate-500">({jobs.length})</span></p>
      {jobs.length ? jobs.map((j, i) => <JobCard key={j.id} job={j} i={`${id}-${i}`} />)
        : <p className="rounded-xl border border-dashed p-5 text-center text-sm text-slate-500">{empty}</p>}
    </section>
  );
}

function AvailabilityToggle({ day, avail, onChange }) {
  const on = avail.includes(day);
  const toggle = async () => onChange((await api.post("/tech/availability", { date: day, available: !on })).data);
  if (day < todayISO()) return null;
  return (
    <button onClick={toggle} data-testid="availability-toggle"
      className={`w-full rounded-full border-2 py-2 text-sm font-semibold ${on ? "border-[#61a229] bg-[#61a229] text-white" : "border-dashed border-slate-300 text-slate-600"}`}>
      {on ? "Disponibile in questo giorno ✓ (tocca per togliere)" : "Segna come disponibile"}
    </button>
  );
}

export default function TechAgenda() {
  const { user } = useAuth();
  const autonomo = user?.employment === "autonomo";
  const [avail, setAvail] = useState([]);
  useEffect(() => { if (autonomo) api.get("/tech/availability").then((r) => setAvail(r.data)); }, [autonomo]);
  const [jobs, setJobs] = useState(null);
  const [day, setDay] = useState(null);
  useEffect(() => { api.get("/tech/jobs").then((r) => setJobs(r.data)); }, []);
  const today = todayISO();
  const all = jobs || [];
  const todays = all.filter((j) => j.date === today);
  const next = all.filter((j) => j.date > today && j.status !== "completato").slice(0, 10);
  const picked = day ? all.filter((j) => j.date === day) : [];

  return (
    <div className="space-y-5">
      <div className="rise">
        <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#61a229]">La tua agenda</p>
        <h1 className="mt-1 font-display text-3xl font-bold capitalize">{fmtLongDate(today)}</h1>
      </div>
      {jobs === null ? <p className="text-sm text-slate-500"><Clock className="mr-1 inline h-4 w-4" />Caricamento…</p> : (
        <>
          <Section title="Oggi" jobs={todays} id="agenda-today" empty="Nessun intervento oggi." highlight />
          <MonthCalendar jobs={all} selected={day} onSelect={(d) => setDay(d === day ? null : d)} available={avail} />
          {autonomo && <p className="text-xs text-slate-500">Tocca un giorno del calendario per indicare all'ufficio la tua disponibilità.</p>}
          {day && autonomo && <AvailabilityToggle day={day} avail={avail} onChange={setAvail} />}
          {day && <Section title={fmtLongDate(day)} jobs={picked} id="agenda-day" empty="Nessun intervento in questo giorno." />}
          <Section title="Prossimi" jobs={next} id="agenda-upcoming" empty="Nessun intervento in programma." />
        </>
      )}
    </div>
  );
}
